<template>
  <donut-form-item
    label="Your dates"
    description="Set your birthday and work anniversary dates to receive team celebrations"
    :left-align="true"
    class="m-tx2"
  >
    <div class="f-h f-ac f-sb">
      <div class="f-h f-ac w-50p m-rx2">
        <div class="t-sm m-rx2">Birthday</div>
        <div class="flexible">
          <donut-selector-single-static
            v-model="birthdaySelection"
            placeholder="Select a birthday"
            :options="birthdayOptions"
            :disabled="loading"
            :loading="loading"
            :allow-clear="true"
            class="w-100p"
          />
        </div>
      </div>
      <div class="f-h f-ac w-50p m-lx2">
        <div class="t-sm m-rx2">Start date</div>
        <div
          v-tooltip="restrictEditing ? 'You can\'t edit your start date because your team is using automated Milestone Points. Ask a Donut admin to edit this for you.' : null"
          class="flexible"
        >
          <donut-datepicker
            v-model="anniversarySelection"
            placeholder="Select a start date"
            :loading="loading"
            :clear-btn="true"
            :disabled="loading || restrictEditing"
            class="w-100p"
          />
        </div>
      </div>
    </div>
  </donut-form-item>
  <donut-form-item
    label="Birthday messages"
    description="Donut will send a birthday message on the Celebrations channels you are part of.  Don't worry about the year, Donut doesn't save that information. 😄"
    :left-align="true"
    class="m-tx2"
  >
    <div class="f-h f-ac f-sb">
      <donut-selector-multi-static
        v-if="birthdayChannelOptions.length > 0"
        v-model="selectedBirthdayChannels"
        :options="birthdayChannelOptions"
        placeholder="Select channels for birthday messages"
        :key-map="{ value: 'id', label: 'name' }"
        class="w-100p"
      />
      <div v-else>
        <div class="t-sm">You’re not a member of any Celebrations channels in your workspace, or none have been created yet.</div>
      </div>
    </div>
    <div v-if="managerReminderNote" class="f-h f-ac m-tx1">
      <i class="mi md-16 gray m-r">info</i>
      <div class="t-xs gray">{{ managerReminderNote }}</div>
    </div>
  </donut-form-item>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import * as moment from 'moment';

const { dispatch, getters } = useStore();

const loading = computed(() => getters['celebrationSchedules/loading']);
const managerReminderNote = computed(() => {
  const types = getters['celebrationSchedules/managerReminderTypes'];
  if (!types.length) return null;
  const occasions = types.length > 1 ? 'birthday and anniversary dates' : types[0];
  return `Even with no channels selected, your manager may get reminders before your ${occasions}`;
});
const restrictEditing = computed(() => getters['celebrationSchedules/editingRestrictedTypes'].includes('anniversary'));

// Flag to prevent API calls during initialization
const handlersEnabled = ref(false);

// Track last processed value to prevent duplicates since the datepicker seems to trigger multiple rapid updates
const lastAnniversaryValue = ref(null);

const availableBirthdayChannels = computed(() => {
  const channels = getters['celebrationSchedules/availableBirthdayChannels'];
  return channels;
});

const selectedBirthdayChannelIds = computed(() => {
  const ids = getters['celebrationSchedules/selectedBirthdayChannelIds'];
  return ids;
});

const birthdayChannelOptions = computed(() => {
  const channels = availableBirthdayChannels.value;
  const result = channels.map(channel => ({
    id: channel.id,
    name: channel.name,
    uid: channel.uid,
  }));

  return result;
});

const selectedBirthdayChannels = computed({
  get: () => {
    const selectedIds = selectedBirthdayChannelIds.value;
    const availableChannels = birthdayChannelOptions.value;
    return availableChannels.filter(channel => selectedIds.includes(channel.id));
  },
  set: (newChannels) => {
    // Skip if handlers are not enabled yet
    if (!handlersEnabled.value) return;

    const newIds = newChannels.map(channel => channel.id).sort();
    const currentIds = [...selectedBirthdayChannelIds.value].sort();

    // Check if selection actually changed to prevent unnecessary API calls
    const hasChanged = newIds.length !== currentIds.length
      || newIds.some((id, index) => id !== currentIds[index]);

    if (!hasChanged) return;

    // Get current birthday schedule to preserve date values
    const currentBirthdaySchedule = getters['celebrationSchedules/birthdaySchedule'];

    dispatch('celebrationSchedules/upsertMySchedule', {
      celebration_type: 'birthday',
      selected_channel_ids: newIds,
      // Preserve current birthday dates when updating channels
      month: currentBirthdaySchedule?.month || null,
      day: currentBirthdaySchedule?.day || null,
      year: currentBirthdaySchedule?.year || null,
      source: 'birthday channels change',
    });
  },
});

// Generate all birthday options (month + day combinations)
const birthdayOptions = computed(() => {
  const options = [];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  months.forEach((month, monthIndex) => {
    // Use a leap year to get the maximum possible days for February
    const daysInMonth = moment(`2024-${monthIndex + 1}`, 'YYYY-M').daysInMonth();
    for (let day = 1; day <= daysInMonth; day += 1) {
      const monthDay = `${monthIndex + 1}-${day}`;
      options.push({
        label: `${month} ${day}`,
        value: monthDay,
      });
    }
  });

  return options;
});

const birthdaySelection = computed({
  get: () => {
    const schedule = getters['celebrationSchedules/birthdaySchedule'];
    if (!schedule) return null;
    const value = `${schedule.month}-${schedule.day}`;
    // Find the matching option object
    const option = birthdayOptions.value.find(opt => opt.value === value);
    return option || null;
  },
  set: (option) => {
    // Skip if handlers are not enabled yet
    if (!handlersEnabled.value) return;

    // Skip if value hasn't actually changed to prevent unnecessary API calls
    const currentSchedule = getters['celebrationSchedules/birthdaySchedule'];
    if (currentSchedule && option) {
      const currentValue = `${currentSchedule.month}-${currentSchedule.day}`;
      if (option.value === currentValue) return;
    }

    if (!option || !option.value || option.value === '') {
      // Clear birthday - send null values (don't include channel params)
      dispatch('celebrationSchedules/upsertMySchedule', {
        celebration_type: 'birthday',
        month: null,
        day: null,
        year: null,
        source: 'birthday change clear',
      });
    } else {
      // Parse the value (format: "7-5" for July 5th)
      const [month, day] = option.value.split('-');
      dispatch('celebrationSchedules/upsertMySchedule', {
        celebration_type: 'birthday',
        month: parseInt(month, 10),
        day: parseInt(day, 10),
        year: null, // Birthday never has a year so we should not persist it
        source: 'birthday change set',
      });
    }
  },
});

const anniversarySelection = computed({
  get: () => {
    const schedule = getters['celebrationSchedules/anniversarySchedule'];
    if (!schedule) return '';

    // Parse the date from the schedule and format it without timezone conversion
    const { year, month, day } = schedule;
    const paddedMonth = String(month).padStart(2, '0');
    const paddedDay = String(day).padStart(2, '0');
    return moment(`${year}-${paddedMonth}-${paddedDay}`).format('MMM D, YYYY');
  },
  set: (value) => {
    // Skip if handlers are not enabled yet
    if (!handlersEnabled.value) return;

    // Skip if this is the same value we just processed
    if (value === lastAnniversaryValue.value) return;

    // Skip if value hasn't actually changed from store to prevent unnecessary API calls
    const currentSchedule = getters['celebrationSchedules/anniversarySchedule'];
    if (currentSchedule) {
      const currentValue = moment(`${currentSchedule.year}-${String(currentSchedule.month).padStart(2, '0')}-${String(currentSchedule.day).padStart(2, '0')}`).format('MMM D, YYYY');
      if (value === currentValue) return;
    }

    // Update the last processed value
    lastAnniversaryValue.value = value;

    if (!value || value === '') {
      // Clear anniversary - send null values
      dispatch('celebrationSchedules/upsertMySchedule', {
        celebration_type: 'anniversary',
        month: null,
        day: null,
        year: null,
        source: 'anniversary change clear',
      });
    } else {
      // Parse the date - try MM/DD/YYYY format first, then MMM D, YYYY format
      let parsedDate = moment(value, 'MM/DD/YYYY', true);
      if (!parsedDate.isValid()) {
        parsedDate = moment(value, 'MMM D, YYYY', true);
      }
      dispatch('celebrationSchedules/upsertMySchedule', {
        celebration_type: 'anniversary',
        month: parsedDate.month() + 1, // moment months are 0-based
        day: parsedDate.date(),
        year: parsedDate.year(),
        source: 'anniversary change set',
      });
    }
  },
});

onMounted(async () => {
  await dispatch('celebrationSchedules/fetchMySchedules');
  // Enable handlers after data is loaded
  handlersEnabled.value = true;
});
</script>
